import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import { Div, Text } from '@pinpoint/design-kit';
import { charts, components } from '@pinpoint/onpoint-charts';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';

const eventEmitter = new components.EventEmitter();

function Header({ currentQuantiles, quantiles, setQuantiles }) {
  return (
    <Div d="flex" justify="space-between" align="flex-start" m={{ t: '1rem', x: '1rem' }}>
      <Div d="flex" justify="flex-start" align="center" data-test="dist-perc-btns">
        <Text textSize="title" textColor="tabSelected" style={{ fontWeight: 400 }} p={{ r: '1.5rem' }} flexGrow="1">
          Lift Calculations
        </Text>
        {currentQuantiles.map((currentQuantile) => (
          <Button
            buttonStyle="tab"
            key={currentQuantile.length}
            selected={quantiles === currentQuantile}
            p={{ x: '1.5em' }}
            w="1.875rem"
            data-tip={CONSTANTS.TAL_MODEL.QUANTILE_LABEL[currentQuantile]}
            onClick={() => {
              setQuantiles(currentQuantile);
            }}
          >
            {currentQuantile}
          </Button>
        ))}
      </Div>
    </Div>
  );
}

Header.propTypes = {
  currentQuantiles: PropTypes.arrayOf(PropTypes.number).isRequired,
  quantiles: PropTypes.number.isRequired,
  setQuantiles: PropTypes.func.isRequired,
};

export default function ClassifierMode({
  modelView: {
    model,
    quantiles,
    quantileIdx,
    availableQuantiles: { current },
    gradient: gradientProp,
  },
  setQuantiles,
  setGradient: setGradientProp,
}) {
  const [gradient, setGradient] = useState(gradientProp);
  const liftScore = useMemo(
    () =>
      get(
        model,
        `modelData.dataPayload.lift_scores[${quantileIdx}]`,
        get(model, `modelData.dataPayload.liftScores[${quantileIdx}]`, false)
      ),
    [model, quantileIdx]
  );
  const liftDistribution = get(model, 'modelData.dataPayload.lift_distribution', []);
  const interval = useMemo(() => liftDistribution.slice().sort((a, b) => a.left - b.left), [liftDistribution]);
  const totalCount = useMemo(() => interval.map(({ count }) => count).reduce((acc, cur) => acc + cur, 0), [interval]);
  const formatTooltip = useCallback(
    ({ data }) => {
      if (!data) return null;
      const customerCount = data[1].toFixed(0);
      const customerPerc = ((data[1] * 100.0) / totalCount).toFixed(2);
      const intervalLeft = Math.round(parseFloat(data[3]));
      const intervalRight = Math.round(parseFloat(data[0]));
      const liftMult = (data[2] === 0 || data[2] >= 1 ? data[2] : -1 / data[2]).toFixed(2);
      return `<div style="padding: .75rem 1rem;line-height: 1.4;font-size:1rem">
            Number of Customers: ${customerCount}
            <br />
            Percentage of Customers: ${customerPerc}%
            <br />
            Lift: ${liftMult}X
            <br />
            Thinkalike Score: ${intervalLeft} - ${intervalRight}
          </div>`;
    },
    [totalCount]
  );

  useEffect(() => {
    setGradient(gradientProp);
  }, [gradientProp]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <>
      <Header currentQuantiles={current} quantiles={quantiles} setQuantiles={setQuantiles} />
      {liftScore && (
        <Div m={{ b: '1rem', x: '1rem' }} flexGrow="1" overflow="hidden" data-test="div-inner">
          <charts.Histogram
            intervals={interval}
            countSeries={{
              name: 'Frequency',
              values: interval ? interval.map(({ count }) => count) : [],
            }}
            liftSeries={{
              name: 'Lift',
              minInterval: liftScore.min_probability,
              values: liftScore.lift,
            }}
            onChange={(users, usersPercentage, liftAverage, thinkalikeScoreAverage) => {
              eventEmitter.dispatch('stats', {
                users,
                usersPercentage,
                liftAverage,
                thinkalikeScoreAverage,
              });
            }}
            gradient={gradient}
            onGradientChange={(nextGradient) => {
              setGradient(nextGradient);
              setGradientProp(nextGradient);
            }}
            options={{
              grid: {
                top: '20%',
                bottom: '15%',
              },
              tooltip: {
                formatter: formatTooltip,
              },
              xAxis: {
                name: 'Score: predicted similarity to HIT training set',
              },
            }}
          />
        </Div>
      )}
    </>
  );
}

ClassifierMode.propTypes = {
  modelView: PropTypes.shape({
    availableQuantiles: PropTypes.shape({
      current: PropTypes.arrayOf(PropTypes.number).isRequired,
    }).isRequired,
    quantiles: PropTypes.number.isRequired,
    quantileIdx: PropTypes.number.isRequired,
    model: ModelProp.isRequired,
    gradient: PropTypes.string.isRequired,
  }).isRequired,
  setQuantiles: PropTypes.func.isRequired,
  setGradient: PropTypes.func.isRequired,
};
